import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios'
import i18n from '@/i18n'
import { useUserStore } from '@/manage/stores/user'
import { logger } from '@/utils/logger'
import { appStorage } from '@/utils/storage/app'
import { notifyHttpStatusError, notifyNetworkError } from '@/utils/httpError'
import { redirectToOfficialSite } from '@/utils/authRedirect'
import { envConfig } from '@/shared/config/env'

const RETRY_DELAY_BASE_MS = 300
const MAX_TIMEOUT_RETRY = 1
const RECENT_SUCCESS_WINDOW_MS = 60_000
const NETWORK_TOAST_INTERVAL_MS = 3_000
const TRANSIENT_STATUSES = new Set([408, 429, 502, 503, 504])
const RETRYABLE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

type RetryConfig<D = unknown> = AxiosRequestConfig<D> & {
  __retryCount?: number
  __silenceGlobalError?: boolean
  __authRetry?: boolean
  __accessContext?: string
}
type HttpService = Omit<
  AxiosInstance,
  'request' | 'get' | 'delete' | 'head' | 'options' | 'post' | 'put' | 'patch'
> & {
  request<T = unknown, D = unknown>(config: RetryConfig<D>): Promise<T>
  get<T = unknown, D = unknown>(url: string, config?: RetryConfig<D>): Promise<T>
  delete<T = unknown, D = unknown>(url: string, config?: RetryConfig<D>): Promise<T>
  head<T = unknown, D = unknown>(url: string, config?: RetryConfig<D>): Promise<T>
  options<T = unknown, D = unknown>(url: string, config?: RetryConfig<D>): Promise<T>
  post<T = unknown, D = unknown>(url: string, data?: D, config?: RetryConfig<D>): Promise<T>
  put<T = unknown, D = unknown>(url: string, data?: D, config?: RetryConfig<D>): Promise<T>
  patch<T = unknown, D = unknown>(url: string, data?: D, config?: RetryConfig<D>): Promise<T>
}

type HeaderAccessor = Record<string, unknown> & {
  get?: (name: string) => unknown
  set?: (name: string, value: string) => void
}

let lastNetworkToastAt = 0
const getLangHeader = () => {
  const currentLocale = i18n.global.locale.value || appStorage.getLocale()
  return String(currentLocale).startsWith('en') ? 'en' : 'zh'
}

const getErrorMessage = (error: unknown) => {
  if (typeof error === 'object' && error && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string') return message
  }
  return ''
}

const getErrorCode = (error: unknown) => {
  if (typeof error === 'object' && error && 'code' in error) {
    const code = (error as { code?: unknown }).code
    if (typeof code === 'string') return code
  }
  return ''
}

const isTimeoutLike = (error: unknown) => {
  const message = getErrorMessage(error).toLowerCase()
  const code = getErrorCode(error)
  return code === 'ECONNABORTED' || code === 'ETIMEDOUT' || message.includes('timeout')
}

const isTransientError = (error: unknown) => {
  const status = axios.isAxiosError(error) ? Number(error.response?.status || 0) : 0
  const message = getErrorMessage(error).toLowerCase()
  const code = getErrorCode(error)
  return (
    !axios.isAxiosError(error) ||
    !error.response ||
    isTimeoutLike(error) ||
    code === 'ERR_NETWORK' ||
    message.includes('network error') ||
    TRANSIENT_STATUSES.has(status)
  )
}

const showNetworkErrorToast = () => {
  const now = Date.now()
  if (now - lastNetworkToastAt < NETWORK_TOAST_INTERVAL_MS) return
  lastNetworkToastAt = now
  notifyNetworkError('request-service')
}

const stableSerialize = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  if (typeof value !== 'object') return JSON.stringify(value)

  const normalize = (input: unknown): unknown => {
    if (Array.isArray(input)) return input.map((item) => normalize(item))
    if (input && typeof input === 'object') {
      return Object.keys(input as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((acc, key) => {
          acc[key] = normalize((input as Record<string, unknown>)[key])
          return acc
        }, {})
    }
    return input
  }

  return JSON.stringify(normalize(value))
}

const getAccessContext = () => {
  const user = useUserStore()
  return JSON.stringify([user.user?.sub, user.isAdminModeActive])
}

const createService = (baseURL: string): HttpService => {
  const service = axios.create({
    baseURL,
    timeout: 12000,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  })

  const pendingRequests = new Map<string, AbortController>()
  let lastSuccessAt = 0

  const makeKey = (config: RetryConfig) => {
    const method = String(config.method || 'get').toUpperCase()
    const url = String(config.url || '')
    const params = stableSerialize(config.params)
    const data = typeof config.data === 'string' ? config.data : stableSerialize(config.data)
    return `${config.__accessContext}|${method}|${url}|${params}|${data}`
  }

  const clearPending = (config: RetryConfig) => {
    const key = makeKey(config)
    if (pendingRequests.get(key)?.signal === config.signal) pendingRequests.delete(key)
  }

  service.interceptors.request.use(
    async (config) => {
      config.headers = config.headers || {}
      const headersAny = config.headers as HeaderAccessor
      const hasLangHeader =
        headersAny['x-easyhire-lang'] ??
        headersAny['X-EASYHIRE-LANG'] ??
        (typeof headersAny.get === 'function' ? headersAny.get('x-easyhire-lang') : undefined)
      if (!hasLangHeader) {
        if (typeof headersAny.set === 'function') {
          headersAny.set('x-easyhire-lang', getLangHeader())
        } else {
          headersAny['x-easyhire-lang'] = getLangHeader()
        }
      }

      const userStore = useUserStore()
      if (userStore.isSigningOut) {
        throw new axios.CanceledError('Session is signing out')
      }
      const accessConfig = config as RetryConfig
      accessConfig.__accessContext ??= getAccessContext()
      if (accessConfig.__accessContext !== getAccessContext())
        throw new axios.CanceledError('Access context changed')
      const token = await userStore.getFreshToken(undefined, {
        allowStaleOnNetworkError: true,
        reason: 'request_interceptor',
      })
      if (userStore.isSigningOut) {
        throw new axios.CanceledError('Session is signing out')
      }
      if (accessConfig.__accessContext !== getAccessContext())
        throw new axios.CanceledError('Access context changed')
      if (token) {
        if (typeof headersAny.set === 'function') {
          headersAny.set('Authorization', `Bearer ${token}`)
        } else {
          headersAny['Authorization'] = `Bearer ${token}`
        }
      }

      config.headers.delete('X-Admin-Mode')
      if (userStore.isAdminModeActive) {
        if (typeof headersAny.set === 'function') {
          headersAny.set('X-Admin-Mode', 'true')
        } else {
          headersAny['X-Admin-Mode'] = 'true'
        }
      }

      const retryConfig = config as RetryConfig
      const key = makeKey(retryConfig)
      const method = String(config.method || 'get').toUpperCase()
      const enableInFlightDedupe = RETRYABLE_METHODS.has(method)
      if (enableInFlightDedupe && pendingRequests.has(key)) {
        const controller = pendingRequests.get(key)
        controller?.abort()
        pendingRequests.delete(key)
      }

      const controller = new AbortController()
      config.signal = controller.signal
      if (enableInFlightDedupe) {
        pendingRequests.set(key, controller)
      }

      if (typeof config.url === 'string' && config.url.includes('/enum/location')) {
        config.timeout = Math.max(config.timeout || 0, 20000)
      }

      retryConfig.__retryCount = retryConfig.__retryCount || 0
      return config
    },
    async (error) => {
      logger.debug('Request interceptor error:', error)
      return Promise.reject(error)
    },
  )

  service.interceptors.response.use(
    (response) => {
      clearPending(response.config as RetryConfig)
      if (useUserStore().isSigningOut) {
        throw new axios.CanceledError('Session is signing out')
      }
      if ((response.config as RetryConfig).__accessContext !== getAccessContext())
        throw new axios.CanceledError('Access context changed')
      lastSuccessAt = Date.now()
      return response.data
    },
    async (error) => {
      if (error.config) clearPending(error.config as RetryConfig)
      if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
        return Promise.reject(error)
      }

      if (useUserStore().isSigningOut) {
        return Promise.reject(new axios.CanceledError('Session is signing out'))
      }

      logger.debug('Response interceptor error:', error)

      const { response } = error
      const cfg = (error.config || {}) as RetryConfig
      if (cfg.__accessContext && cfg.__accessContext !== getAccessContext())
        return Promise.reject(new axios.CanceledError('Access context changed'))

      const method = String(cfg.method || 'GET').toUpperCase()
      const hasRecentSuccess = Date.now() - lastSuccessAt < RECENT_SUCCESS_WINDOW_MS
      const retryLimit = RETRYABLE_METHODS.has(method) || hasRecentSuccess ? MAX_TIMEOUT_RETRY : 0
      const retryCount = cfg.__retryCount || 0
      const shouldRetry = isTransientError(error) && retryCount < retryLimit

      if (shouldRetry) {
        cfg.__retryCount = retryCount + 1
        const delay = RETRY_DELAY_BASE_MS * cfg.__retryCount
        return new Promise((resolve) => setTimeout(resolve, delay)).then(() => service.request(cfg))
      }

      const silenceGlobalError = Boolean(cfg.__silenceGlobalError)

      if (response) {
        if (response.status === 401 && !cfg.__authRetry) {
          const userStore = useUserStore()
          cfg.__authRetry = true

          try {
            const refreshedToken = await userStore.forceRefreshDefaultToken()
            if (refreshedToken) {
              return service.request(cfg)
            }
          } catch (refreshError) {
            logger.warn('401 retry after token refresh failed:', refreshError)
          }
        }

        if (silenceGlobalError && response.status !== 401) {
          return Promise.reject(error)
        }
        if (useUserStore().isSigningOut) {
          return Promise.reject(new axios.CanceledError('Session is signing out'))
        }
        switch (response.status) {
          case 401:
            notifyHttpStatusError(401, undefined, { scope: 'request-service' })
            useUserStore().clearUser()
            redirectToOfficialSite('apiUnauthorized')
            break
          default:
            notifyHttpStatusError(response.status, response.data?.msg || response.data?.message, {
              scope: 'request-service',
            })
        }
      } else {
        if (silenceGlobalError) {
          return Promise.reject(error)
        }
        showNetworkErrorToast()
      }

      return Promise.reject(error)
    },
  )

  return service as unknown as HttpService
}

// used for /api/v1
export const apiV1Service = createService(envConfig.apiV1BaseUrl)

// used for /api/v2
export const apiV2Service = createService('/api/v2')

// factory export for custom service instances
export default createService
